/**
 * Fetches the current Twitch chatter list (with roles) from /api/config/chatters.
 * Only polls while `enabled` is true (panel is open).
 * Poll interval matches the viewer-count refresh rate (30 s).
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api.js';

export type ChatterRole = 'broadcaster' | 'mod' | 'vip' | 'viewer';

export interface Chatter {
  user_id: string;
  user_login: string;
  user_name: string;
  role: ChatterRole;
}

export function useChatters(enabled: boolean, pollMs = 30_000) {
  const [chatters, setChatters] = useState<Chatter[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch<{ total: number; chatters: Chatter[] }>('/api/config/chatters');
      setChatters(r.chatters); // already sorted by server
      setTotal(r.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void doFetch();
    const id = setInterval(() => void doFetch(), pollMs);
    return () => clearInterval(id);
  }, [enabled, doFetch, pollMs]);

  return { chatters, total, loading, error };
}
